// SupportChat.js
// ---------------------------------------------------------------------------
// The panel behind the Support button, top-right of the platform.
//
// One shared thread per organisation (see the backend migration for why
// it's per-group rather than per-user) — any manager at this org sees the
// same conversation with our support team. Genuinely two-way: a manager
// types here; a reply typed by support elsewhere (see
// support.internal.routes.js on the API) shows up on the next poll.
//
// NO REAL-TIME TRANSPORT. This backend has no websocket/SSE infrastructure
// anywhere (checked — FleetMap.js polls for the same reason), so this panel
// polls GET /api/support/thread every few seconds while it's open, and
// App.js separately polls GET /api/support/unread-count on a slower
// interval the whole time you're signed in, for the badge. If this ever
// gets busy enough to want push-style delivery, both loops are isolated
// here and in App.js's useSupportUnread hook, not spread through the app.
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "./apiBase";

const OPEN_POLL_MS = 6000;

async function api(path, options = {}) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE}/api/support${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Failed (${res.status})`);
  }
  return data;
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T") + (iso.endsWith("Z") ? "" : "Z"));
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return sameDay ? time : `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${time}`;
}

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9500,
    background: "rgba(15, 23, 42, 0.18)",
  },
  panel: {
    position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9501,
    width: "min(380px, 100vw)", background: "#fff",
    boxShadow: "-8px 0 28px rgba(15,23,42,0.18)",
    display: "flex", flexDirection: "column",
    fontFamily: "inherit",
  },
  head: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "14px 16px", borderBottom: "1px solid #e5e7eb",
    background: "#0f766e", color: "#fff", flexShrink: 0,
  },
  headTitle: { fontSize: 15, fontWeight: 700, flex: 1 },
  closeBtn: {
    border: "none", background: "rgba(255,255,255,0.15)", color: "#fff",
    width: 28, height: 28, borderRadius: 6, cursor: "pointer", fontSize: 16,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  body: {
    flex: 1, overflowY: "auto", padding: "14px 12px",
    background: "#f0f4f2", display: "flex", flexDirection: "column", gap: 8,
  },
  bubbleRow: (mine) => ({ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }),
  bubble: (mine) => ({
    maxWidth: "78%", padding: "8px 11px", borderRadius: 12,
    fontSize: 13.5, lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word",
    background: mine ? "#dcf8c6" : "#fff",
    borderBottomRightRadius: mine ? 3 : 12,
    borderBottomLeftRadius: mine ? 12 : 3,
    boxShadow: "0 1px 1.5px rgba(0,0,0,0.08)",
    color: "#111827",
  }),
  meta: { fontSize: 10, color: "#94a3b8", marginTop: 3, textAlign: "right" },
  empty: { margin: "auto", color: "#94a3b8", fontSize: 13, textAlign: "center", padding: 24 },
  error: { margin: "8px 12px", padding: "8px 10px", background: "#fef2f2",
    border: "1px solid #fecaca", color: "#991b1b", borderRadius: 6, fontSize: 12.5 },
  footer: {
    display: "flex", gap: 8, padding: "10px 12px", borderTop: "1px solid #e5e7eb",
    background: "#fff", flexShrink: 0,
  },
  input: {
    flex: 1, resize: "none", border: "1px solid #d8dee8", borderRadius: 18,
    padding: "9px 14px", fontSize: 13.5, fontFamily: "inherit", maxHeight: 90,
    outline: "none",
  },
  sendBtn: {
    border: "none", background: "#0f766e", color: "#fff", width: 38, height: 38,
    borderRadius: "50%", cursor: "pointer", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  sendBtnDisabled: { background: "#9ca3af", cursor: "not-allowed" },
};

function SendIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4 20-7z" />
    </svg>
  );
}

/** @param {{ onClose: () => void, onReadAll: () => void }} props */
export default function SupportChat({ onClose, onReadAll }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  const pollRef = useRef(null);
  const alive = useRef(true);

  const scrollToBottom = useCallback(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await api("/thread");
      if (!alive.current) return;
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setError("");
      if (!opts.silent) requestAnimationFrame(scrollToBottom);
    } catch (e) {
      if (alive.current && !opts.silent) setError(e.message || "Could not load the conversation");
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [scrollToBottom]);

  // On open: load the thread, mark everything read (clears the badge in
  // App.js), then poll quietly while the panel stays open.
  useEffect(() => {
    alive.current = true;
    load();
    api("/thread/read").then(() => onReadAll && onReadAll()).catch(() => {
      // A failed mark-as-read shouldn't block reading the conversation —
      // the badge just stays until the next successful open.
    });
    pollRef.current = setInterval(() => load({ silent: true }), OPEN_POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => messages, [messages]);

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError("");
    // Optimistic: WhatsApp-style chat feels wrong if your own message waits
    // for a round trip before appearing.
    const optimistic = {
      id: `local-${Date.now()}`,
      direction: "to_support",
      body,
      authorName: null,
      readAt: null,
      createdAt: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    requestAnimationFrame(scrollToBottom);
    try {
      const saved = await api("/messages", { method: "POST", body: JSON.stringify({ body }) });
      if (!alive.current) return;
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? saved : m)));
    } catch (e) {
      if (!alive.current) return;
      setError(e.message || "That message didn't send — it's still in the box below.");
      // Put it back in the input rather than silently dropping it.
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(body);
    } finally {
      if (alive.current) setSending(false);
    }
  }, [draft, sending, scrollToBottom]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <div style={S.overlay} onClick={onClose} />
      <div style={S.panel} role="dialog" aria-label="Support chat">
        <div style={S.head}>
          <div style={S.headTitle}>Support</div>
          <button style={S.closeBtn} onClick={onClose} aria-label="Close support chat">×</button>
        </div>

        <div style={S.body} ref={bodyRef}>
          {loading && <div style={S.empty}>Loading…</div>}
          {!loading && grouped.length === 0 && (
            <div style={S.empty}>
              Send us a message and we'll get back to you here.
            </div>
          )}
          {grouped.map((m) => {
            const mine = m.direction === "to_support";
            return (
              <div key={m.id} style={S.bubbleRow(mine)}>
                <div>
                  <div style={{ ...S.bubble(mine), opacity: m._pending ? 0.65 : 1 }}>
                    {m.body}
                  </div>
                  <div style={S.meta}>
                    {m._pending ? "Sending…" : fmtTime(m.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {error && <div style={S.error}>{error}</div>}

        <div style={S.footer}>
          <textarea
            style={S.input}
            rows={1}
            placeholder="Message support…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button
            style={{ ...S.sendBtn, ...(!draft.trim() || sending ? S.sendBtnDisabled : {}) }}
            onClick={send}
            disabled={!draft.trim() || sending}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </>
  );
}
